import React, { useMemo, useState } from 'react'

import { useAccountActions } from 'redux/actions/Accounts'

import { MemberStatus, TGroup } from 'types/GlobalGroup'
import { EAccountRole, TAccount } from 'types/GlobalAccount'

import { useGroups } from 'redux/hooks/Groups'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useAccounts, useRoles } from 'redux/hooks/Accounts'
import { useTranslation } from 'language/translator'
import { useAllowInternalFollowUp, useCustomer } from 'redux/hooks/Customer'
import {
  useCompanyGroupsData,
  useCompanyGroupsFetched
} from 'redux/hooks/CompanyGroups'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { formatGroupOption, formatUsername } from 'utils/functions'
import { getParsedDate } from 'utils/dateParser'

import Banner from 'components_new/molecules/Banner'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import ChipSelect from 'components_new/molecules/ChipSelect'
import ChipTabs from 'components_new/molecules/ChipTabs'
import GroupAvatar from 'components_new/molecules/GroupAvatar'
import IconButton from 'components_new/atoms/IconButton'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SearchField from 'components_new/molecules/SearchField'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import AddAccount from 'components_new/organisms/AddAccount'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import EditUserDialog from 'components_new/organisms/dialogs/EditUserDialog'

import SettingsTemplate from 'components_new/templates/SettingsTemplate'

import { useIsMobile } from 'utils/hooks'
import {
  useDefaultOrganizationalUnits,
  useFullAccessEnum,
  useOrganizationalUnitsArray,
  useOrganizationalUnitsFetched
} from 'redux/hooks/OrganizationalUnits'

const UsersPage = () => {
  const { tryUpdateUser, tryDeleteUser } = useAccountActions()

  const { lang, t, translateEnum } = useTranslation()

  const customer = useCustomer()

  const { allGroups } = useGroups()
  const companyGroups = useCompanyGroupsData()
  const companyGroupsFetched = useCompanyGroupsFetched()
  const { account: signedInAccount, isAdmin, isHomepal } = useAccountSettings()
  const isMobile = useIsMobile()
  const { roles, loading: loadingRoles } = useRoles()
  const { accounts, accountBody, loading: accountsLoading } = useAccounts(true)
  const organizationalUnits = useOrganizationalUnitsArray()
  const defaultOrganizationalUnits = useDefaultOrganizationalUnits()
  const fullAccessEnum = useFullAccessEnum()
  const fetchedOrgUnits = useOrganizationalUnitsFetched()
  const allowInternalFollowUp = useAllowInternalFollowUp()

  const groupMapper = useMemo(() => {
    const mapper: { [accountId: string]: TGroup[] } = {}

    accounts.forEach((acc) => {
      mapper[acc.id] = allGroups.filter((g) =>
        g.members.find(
          (m) => m.account_id === acc.id && m.status === MemberStatus.APPROVED
        )
      )
    })

    return mapper
  }, [allGroups, accounts])

  const [editUserId, setEditUserId] = useState<string | null>(null)
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null)

  const [inviteUserDialogOpen, setInviteUserDialogOpen] =
    useState<boolean>(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [role, setRole] = useState<EAccountRole | null>(null)

  const filteredUsers = accounts.filter(
    (acc) =>
      ((formatUsername(acc)
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        acc.email.toLowerCase().includes(searchQuery.toLowerCase())) &&
        !role) ||
      acc.role === role
  )

  const userToDelete = useMemo(() => {
    if (deleteUserId) {
      const acc = accountBody[deleteUserId]

      return acc ? formatUsername(acc) : null
    }

    return ''
  }, [deleteUserId])

  const orgOptions = useMemo(
    () => [
      ...defaultOrganizationalUnits.map((ou) => {
        return {
          id: ou.id,
          title: ou.name[lang]
        }
      }),
      ...organizationalUnits.map((ou) => {
        return {
          id: ou.id,
          title: ou.name
        }
      })
    ],
    [organizationalUnits, t]
  )

  // Helper function to get organizational unit value for a user
  const getOrgUnitValue = (acc: TAccount) => {
    let orgUnit = {
      id: defaultOrganizationalUnits.find((ou) => ou.id !== fullAccessEnum)
        ?.id as string,
      title: t('global', 'none')
    }

    if (!acc.organizational_unit_id) {
      orgUnit = orgOptions.find((ou) => ou.id === fullAccessEnum) || orgUnit
    } else {
      orgUnit =
        orgOptions.find((ou) => ou.id === acc.organizational_unit_id) || orgUnit
    }

    return orgUnit.id
  }

  const edit = isAdmin

  const loading =
    accountsLoading || loadingRoles || !companyGroupsFetched || !fetchedOrgUnits

  // Based on all accounts, not the filtered ones, so that the column does
  // not appear and disappear while searching.
  const hasCompanyGroupUsers = useMemo(
    () => accounts.some((acc) => acc.company_group_id),
    [accounts]
  )

  const renderCompanyGroup = (acc: TAccount) => {
    if (!acc.company_group_id) {
      return <Text color="text.secondary">{'–'}</Text>
    }

    return (
      <Chip
        icon={<Icon name="ApartmentOutlined" />}
        label={
          companyGroups[acc.company_group_id]?.name ||
          t('users', 'company_group_removed_title')
        }
        size="small"
      />
    )
  }

  const renderDataAccess = (acc: TAccount) => {
    // Company group users always see exactly their customer's data, so
    // there is no organizational unit to pick.
    if (acc.company_group_id) {
      return (
        <Tooltip title={t('users', 'company_group_data_access_tooltip')}>
          <Chip
            icon={<Icon name="Access" />}
            label={t('global', 'company_group')}
            size="small"
          />
        </Tooltip>
      )
    }

    if (!allowInternalFollowUp) {
      return (
        <Chip
          icon={<Icon name="Access" />}
          label={t('global', 'all')}
          size="small"
        />
      )
    }

    return (
      <ChipSelect
        disabled={!edit || acc.role === EAccountRole.ADMIN}
        icon={<Icon name="Access" />}
        onChange={(value) => {
          tryUpdateUser(acc.id, {
            data: {
              organizational_unit_id:
                value.id === fullAccessEnum ? null : (value.id as string)
            }
          })
        }}
        options={orgOptions}
        size="small"
        value={getOrgUnitValue(acc)}
        tooltip={
          acc.role === EAccountRole.ADMIN
            ? t('users', 'admin_data_access_tooltip')
            : undefined
        }
      />
    )
  }

  return (
    <>
      <SettingsTemplate
        disableNav={isMobile}
        topbar={
          edit
            ? {
                actions: (
                  <Button
                    onClick={() => setInviteUserDialogOpen(true)}
                    startIcon={<Icon name="Add" />}
                    variant="text"
                  >
                    {t('global', 'add')}
                  </Button>
                )
              }
            : undefined
        }
      >
        <SectionHeader
          gutterBottom={true}
          title={t('global', 'users')}
          titleAdornment={
            role || searchQuery ? (
              <Text color="text.secondary">
                {filteredUsers.length} {t('search', 'results_found', true)}
              </Text>
            ) : null
          }
        />

        {/*-- search --*/}
        <SearchField
          autoFocus={true}
          disabled={loading}
          onClear={() => setSearchQuery('')}
          onSearch={(value) => setSearchQuery(value)}
          placeholder={t('users', 'search_placeholder')}
          size="small"
          sx={{ mb: 1 }}
          value={searchQuery}
        />

        {/*-- filter --*/}
        <ChipTabs
          disabled={loading}
          onChange={(value) => setRole(value as EAccountRole | null)}
          options={Object.values(roles).map((role) => {
            return {
              label: translateEnum('account_roles', role.name).title,
              value: role.name
            }
          })}
          size="small"
          sx={{ mb: 1 }}
          value={role}
        />

        {/*-- table --*/}
        <SimpleTable
          addRow={
            edit
              ? {
                  onClick: () => setInviteUserDialogOpen(true)
                }
              : undefined
          }
          border={true}
          columns={[
            { title: t('global', 'name'), width: 250 },
            { title: t('users', 'role') },
            ...(hasCompanyGroupUsers
              ? [{ title: t('global', 'company_group') }]
              : []),
            { title: t('data_access', 'data_access') },
            { title: t('global', 'groups'), width: 250 },
            { color: 'text.secondary', title: t('global', 'joined') },
            ...(edit
              ? [{ align: 'right' as const, title: t('global', 'actions') }]
              : [])
          ]}
          hover={true}
          loading={loading}
          rows={[...filteredUsers]
            .sort((a, b) =>
              // eslint-disable-next-line max-len
              sortAlphabeticalAsc(formatUsername(a), formatUsername(b))
            )
            .map((acc) => {
              return {
                cells: [
                  <>
                    <GenericListItem
                      body={acc.email}
                      component="div"
                      disablePadding={true}
                      title={
                        acc.first_name
                          ? formatUsername(acc)
                          : t('global', 'unnamed')
                      }
                      variant="user"
                    />
                  </>,
                  <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <ChipSelect
                        disabled={
                          !edit ||
                          acc.email.includes('@stockholm.se') ||
                          acc.email.includes('@stockholmtest.se') ||
                          acc.company_group_id !== null
                        }
                        onChange={(value) => {
                          tryUpdateUser(acc.id, {
                            data: {
                              role: roles[value.id as EAccountRole].id
                            }
                          })
                        }}
                        options={Object.values(roles).map((role) => {
                          return {
                            body: translateEnum('account_roles', role.name)
                              .summary,
                            id: role.name,
                            title: translateEnum('account_roles', role.name)
                              .title
                          }
                        })}
                        size="small"
                        value={acc.role}
                      />
                      {acc.has_access_to_economy_settings ? (
                        <Tooltip title={t('users', 'economy_description')}>
                          <Chip label={t('global', 'economy')} size="small" />
                        </Tooltip>
                      ) : null}
                      {acc.allow_access_to_analyses ? (
                        <Tooltip title={t('users', 'analyses_description')}>
                          <Chip
                            label={t('analyses', 'analyses')}
                            size="small"
                          />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </>,
                  ...(hasCompanyGroupUsers ? [renderCompanyGroup(acc)] : []),
                  renderDataAccess(acc),
                  <Box
                    key={4}
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                  >
                    {groupMapper[acc.id]
                      // eslint-disable-next-line max-len
                      .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
                      .map((group, i) => (
                        <Chip
                          avatar={
                            <GroupAvatar
                              color={group.profile_color}
                              size="xsmall"
                              title={group.name}
                              sx={{ ml: 1 }}
                            />
                          }
                          key={`${acc.id}-${i}`}
                          label={group.name}
                          size="small"
                        />
                      ))}
                  </Box>,
                  <>
                    {acc.invite_accepted_at ? (
                      <Text color="inherit">
                        {getParsedDate(acc.invite_accepted_at, lang)}
                      </Text>
                    ) : (
                      <Tooltip title={t('users', 'not_activated_tooltip')}>
                        <Chip
                          label={t('users', 'not_activated_title')}
                          size="small"
                        />
                      </Tooltip>
                    )}
                  </>,
                  ...(edit
                    ? [
                        <>
                          <IconButton
                            onClick={() => setEditUserId(acc.id)}
                            size="small"
                          >
                            <Icon fontSize="small" name="Edit" />
                          </IconButton>
                          <IconButton
                            disabled={acc.id === signedInAccount?.id}
                            edge="end"
                            onClick={() => setDeleteUserId(acc.id)}
                            size="small"
                          >
                            <Icon fontSize="small" name="Delete" />
                          </IconButton>
                        </>
                      ]
                    : [])
                ]
              }
            })}
          size="small"
        />
      </SettingsTemplate>

      {/*-- dialogs --*/}
      <SimpleDialog
        actions={
          <Button variant="text" onClick={() => setInviteUserDialogOpen(false)}>
            {t('global', 'close')}
          </Button>
        }
        onClose={() => setInviteUserDialogOpen(false)}
        open={inviteUserDialogOpen}
        title={t('global', 'invite')}
      >
        <AddAccount />
      </SimpleDialog>

      {editUserId ? (
        <EditUserDialog
          groups={groupMapper[editUserId].map((item) =>
            formatGroupOption(item, t)
          )}
          id={editUserId}
          onClose={() => setEditUserId(null)}
          updateUser={(body) => {
            if (editUserId) {
              tryUpdateUser(editUserId, body)
            }
          }}
          open
        />
      ) : null}

      <DeleteDialog
        open={deleteUserId !== null}
        onClose={() => setDeleteUserId(null)}
        onDelete={(callback) => {
          if (deleteUserId) {
            tryDeleteUser(deleteUserId)
          }

          setDeleteUserId(null)
          callback()
        }}
        type={t('global', 'user', true)}
        title={userToDelete}
      >
        {
          // eslint-disable-next-line max-len
          !!customer?.has_idp && !isHomepal ? (
            <Banner
              title={t('users', 'sso_title')}
              body={t('users', 'sso_delete_error')}
              iconName={'WarningOutlined'}
              sx={{ bgcolor: 'warning.main' }}
            />
          ) : null
        }
      </DeleteDialog>
    </>
  )
}

export default UsersPage
